import React, { useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Radio, Terminal, PartyPopper } from 'lucide-react';

// --- DATA (Shortened Descriptions) ---
const activitiesData = [
  {
    id: 4,
    title: "Introductory Session",
    category: "Orientation",
    date: "August",
    icon: <Radio className="w-6 h-6 text-purple-400" />,
    desc: "A warm welcome to the new batch, introducing departmental legacy, state-of-the-art labs, and diverse academic opportunities.",
    image: `${process.env.PUBLIC_URL}/images/intro.webp`
  },
  {
    id: 6,
    title: "Freshers 2025",
    category: "Welcome Event",
    date: "September",
    icon: <PartyPopper className="w-6 h-6 text-red-400" />,
    desc: "An energetic evening featuring talent showcases and ice-breaking sessions to foster strong bonds between seniors and juniors.",
    image: `${process.env.PUBLIC_URL}/images/freshers.webp`
  },
];

// --- SKELETON COMPONENT ---
const ActivitySkeleton = () => (
  <div className="h-96 w-full md:w-[48%] lg:w-[30%] rounded-3xl bg-slate-900 border border-white/5 animate-pulse overflow-hidden">
    <div className="h-full w-full bg-slate-800" />
  </div>
);

// --- ACTIVITY CARD ---
const ActivityCard = ({ data, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      whileInView={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      viewport={{ once: true }}
      className="group relative h-96 w-full md:w-[48%] lg:w-[30%] rounded-3xl overflow-hidden shadow-2xl border border-white/10 bg-slate-900"
    >
      <div className="absolute inset-0">
        <img 
          src={data.image} 
          alt={data.title} 
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-slate-900/60 group-hover:bg-slate-900/30 transition-colors duration-500"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent"></div>
      </div>

      <div className="absolute inset-0 p-8 flex flex-col justify-end">
        <div className="mb-auto flex justify-between items-start">
          <span className="px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white bg-white/10 backdrop-blur-md rounded-full border border-white/10">
            {data.category}
          </span>
          <div className="p-2 bg-slate-900/50 rounded-full backdrop-blur-sm border border-white/10">
            {data.icon}
          </div>
        </div>

        <h3 className="text-2xl font-bold text-white mb-2">{data.title}</h3>
        <p className="text-slate-400 text-sm leading-relaxed mb-4 line-clamp-3">
          {data.desc}
        </p>
        
        <div className="flex items-center gap-2 text-cyan-400 text-[10px] font-bold uppercase tracking-widest">
          <Calendar size={14} /> {data.date}
        </div>
      </div>
    </motion.div>
  );
};

const Activities = () => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 800);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section id="activities" className="py-24 bg-slate-950 relative overflow-hidden">
      <div className="absolute inset-0 opacity-10" 
           style={{ backgroundImage: 'linear-gradient(#1e293b 1px, transparent 1px), linear-gradient(90deg, #1e293b 1px, transparent 1px)', backgroundSize: '50px 50px' }}>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-900/20 border border-blue-500/20 text-blue-400 text-xs font-bold uppercase tracking-widest mb-4"
          >
            <Terminal size={14} />
            Departmental Culture
          </motion.div>
          <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-6">
            Events & <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">Engagements</span>
          </h2>
        </div>

        <div className="flex flex-wrap gap-6 justify-center">
          <AnimatePresence mode="wait">
            {isLoading ? (
              [...Array(2)].map((_, i) => <ActivitySkeleton key={i} />)
            ) : (
              activitiesData.map((activity, idx) => (
                <ActivityCard key={activity.id} data={activity} index={idx} />
              ))
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default Activities;